import * as React from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Address, EmploymentGradeOptions, Specialty, SpecialtyRow, User, UserView } from 'cb-model';
import {
  fetchApi,
  getConfigOrThrow,
  makeResetCache,
  selectUser,
  useCacheQuery,
  useCacheRecord,
} from 'gossamer-front-end';
import { QueryOptions } from 'gossamer-unified';
import { Task } from 'gossamer-model';
import { normalizeEmploymentGrade, ProfileYearGroupOptions } from './demographics';

const specialtyQuery: QueryOptions = {
  order: [['label', 'ASC']],
  limit: 500,
};

type Fields = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  date_of_birth: string | null;
  occupation: string | null;
  receive_event_reminders: string;
  line_one?: string | null;
  line_two?: string | null;
  city?: string | null;
  county?: string | null;
  postcode?: string | null;
  country?: string | null;
  year_group?: string | null;
  employment_grade?: string | null;
  specialty?: string | null;
  photo?: string | null;
  person_id?: number;
  address_id?: number | null;
};

const tileStyle: React.CSSProperties = {
  borderRadius: '1rem',
  border: '1px solid #e5e7eb',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
  height: '100%',
};

const descriptionStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: '0.875rem',
  marginBottom: '1rem',
};

const avatarStyle: React.CSSProperties = {
  width: '5rem',
  height: '5rem',
  borderRadius: '1rem',
  objectFit: 'cover',
  background: '#f3f4f6',
  border: '1px solid #d1d5db',
};

const hasRequiredProfileFields = (fields: Fields) =>
  Boolean(fields.photo?.trim()) &&
  Boolean(fields.specialty?.trim()) &&
  Boolean(fields.employment_grade?.trim()) &&
  Boolean(fields.year_group?.trim());

export const WebsiteAccountProfilePage = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const resetCache = React.useMemo(() => makeResetCache(dispatch), [dispatch]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const userRecord = useCacheRecord(UserView, user?.userId || null, !user);
  const addressRecord = useCacheRecord(Address, userRecord.data?.address_id || null, !userRecord.data?.address_id);
  const specialtyRecord = useCacheQuery(Specialty, specialtyQuery);
  const [originalFields, setOriginalFields] = React.useState<Fields | null>(null);
  const [updatedFields, setUpdatedFields] = React.useState<Fields | null>(null);

  const profile = userRecord.data;
  const address = addressRecord.data;
  const specialties = specialtyRecord.data || [];

  React.useEffect(() => {
    if (!profile) return;
    const nextFields: Fields = {
      ...profile,
      employment_grade: normalizeEmploymentGrade(profile.employment_grade) || null,
      specialty: profile.specialty || null,
      year_group: profile.year_group || null,
      line_one: address?.line_one || '',
      line_two: address?.line_two || '',
      postcode: address?.postcode || '',
      city: address?.city || '',
      county: address?.county || '',
      country: address?.country || '',
    };
    setOriginalFields(nextFields);
    setUpdatedFields(nextFields);
  }, [
    profile,
    address?.line_one,
    address?.line_two,
    address?.postcode,
    address?.city,
    address?.county,
    address?.country,
  ]);

  if (!updatedFields || !originalFields) return <Spinner animation="border" />;

  const hasChanges = Object.keys(originalFields).some(
    (key) => originalFields[key as keyof Fields] !== updatedFields[key as keyof Fields],
  );

  const updateField = <K extends keyof Fields>(key: K, value: Fields[K]) => {
    setUpdatedFields((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleAvatarUpload = async (file?: File) => {
    if (!file) return;
    const imgTypes = ['jpg', 'jpeg', 'png', 'gif'];
    if (!imgTypes.includes(file.type.replace('image/', ''))) {
      setMessage(`Only ${imgTypes.map((ext) => ext.toUpperCase()).join(', ')} are supported.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const path = `${getConfigOrThrow('gossamer-be-origin')}${getConfigOrThrow(
      'gossamer-api-path',
    )}/main/binary-upload?filename=${encodeURIComponent(file.name)}`;
    try {
      const response = await fetch(path, {
        credentials: 'include',
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });
      const json = await response.json();
      if (!json.url) {
        throw new Error('Image upload failed.');
      }
      updateField('photo', json.url);
      setMessage(null);
    } catch (error: any) {
      setMessage(error?.message || 'Image upload failed.');
    }
  };

  if (!user) return <Alert variant="danger">Login required.</Alert>;
  if (userRecord.isFetching) return <Spinner animation="border" />;
  if (!userRecord.data) return <Alert variant="danger">Unable to load profile.</Alert>;

  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!hasRequiredProfileFields(updatedFields)) {
          setMessage('Avatar Image, Specialty, Employment Grade, and Year Group are required.');
          return;
        }
        const { email: _email, ...profileUpdate } = updatedFields;
        const payload = {
          ...profileUpdate,
          employment_grade: updatedFields.employment_grade || null,
          specialty: updatedFields.specialty || null,
          year_group: updatedFields.year_group || null,
        };
        setSaving(true);
        setMessage(null);
        try {
          await fetchApi('POST', '/main/update-profile', payload);
          resetCache(UserView.id, profile.id);
          resetCache(User.id, profile.id);
          resetCache(Task.id);
          if (profile.address_id) {
            resetCache(Address.id, String(profile.address_id));
          }
          setOriginalFields(updatedFields);
          setMessage('Profile updated successfully.');
        } catch (err: any) {
          setMessage(err?.message || 'Profile update failed.');
        } finally {
          setSaving(false);
        }
      }}
    >
      <div className="mb-4">
        <p className="text-muted mb-0">Keep your personal, training, and contact details up to date.</p>
      </div>
      <Row className="g-4 align-items-stretch">
        <Col xs={12} lg={6} xl={6} className="d-flex">
          <Card className="w-100" style={tileStyle}>
            <Card.Body>
              <Card.Title as="h3" className="h5">
                Basic Details
              </Card.Title>
              <p style={descriptionStyle}>Your photo and core contact details used across your account.</p>
              <div className="d-flex flex-column gap-3">
                <div>
                  <Form.Label htmlFor="website-account-avatar">Avatar Image *</Form.Label>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    {updatedFields.photo ? (
                      <img src={updatedFields.photo} alt="" style={avatarStyle} />
                    ) : (
                      <div style={avatarStyle} />
                    )}
                    <div className="d-flex flex-column gap-2">
                      <input
                        id="website-account-avatar"
                        name="avatar_image"
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(event) => void handleAvatarUpload(event.target.files?.[0])}
                      />
                      <div className="d-flex flex-wrap gap-2">
                        <Button type="button" variant="outline-secondary" onClick={() => fileInputRef.current?.click()}>
                          {updatedFields.photo ? 'Change avatar' : 'Upload avatar'}
                        </Button>
                        {updatedFields.photo && (
                          <Button type="button" variant="outline-danger" onClick={() => updateField('photo', '')}>
                            Remove
                          </Button>
                        )}
                      </div>
                      <small className="text-muted">JPG, GIF, or PNG. 10MB max.</small>
                    </div>
                  </div>
                </div>
                <Form.Group controlId="website-account-first-name">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    name="first_name"
                    value={updatedFields.first_name || ''}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group controlId="website-account-last-name">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    name="last_name"
                    value={updatedFields.last_name || ''}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    required
                  />
                </Form.Group>
                <Form.Group controlId="website-account-email">
                  <Form.Label>Email</Form.Label>
                  <div
                    id="website-account-email"
                    className="form-control-plaintext px-3 py-2 border rounded-3 bg-light"
                  >
                    {updatedFields.email || 'No email address'}
                  </div>
                </Form.Group>
                <Form.Group controlId="website-account-phone-number">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone_number"
                    value={updatedFields.phone_number || ''}
                    onChange={(e) => updateField('phone_number', e.target.value)}
                  />
                </Form.Group>
                <Form.Group controlId="website-account-date-of-birth">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="date_of_birth"
                    value={updatedFields.date_of_birth?.replace(/T.+/, '') || ''}
                    onChange={(e) => updateField('date_of_birth', e.target.value || null)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={6} xl={6} className="d-flex">
          <Card className="w-100" style={tileStyle}>
            <Card.Body>
              <Card.Title as="h3" className="h5">
                Medical Profile
              </Card.Title>
              <p style={descriptionStyle}>Tell us about your role and training level so we can personalise content.</p>
              <div className="d-flex flex-column gap-3">
                <Form.Group controlId="website-account-specialty">
                  <Form.Label>Specialty *</Form.Label>
                  <Form.Select
                    name="specialty"
                    value={updatedFields.specialty || ''}
                    onChange={(e) => updateField('specialty', e.target.value || null)}
                    required
                  >
                    <option value="">Select specialty</option>
                    {specialties.map((item: SpecialtyRow) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="website-account-current-grade">
                  <Form.Label>Employment Grade *</Form.Label>
                  <Form.Select
                    name="employment_grade"
                    value={updatedFields.employment_grade || ''}
                    onChange={(e) => updateField('employment_grade', e.target.value || null)}
                    required
                  >
                    <option value="">Select employment grade</option>
                    {EmploymentGradeOptions.filter((option) => option.id !== '0').map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="website-account-year-group">
                  <Form.Label>Year Group *</Form.Label>
                  <Form.Select
                    name="year_group"
                    value={updatedFields.year_group || ''}
                    onChange={(e) => updateField('year_group', e.target.value || null)}
                    required
                  >
                    <option value="">Select year group</option>
                    {ProfileYearGroupOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group controlId="website-account-occupation">
                  <Form.Label>Occupation</Form.Label>
                  <Form.Control
                    name="occupation"
                    value={updatedFields.occupation || ''}
                    onChange={(e) => updateField('occupation', e.target.value || null)}
                  />
                </Form.Group>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={6} xl={4} className="d-flex">
          <Card className="w-100" style={tileStyle}>
            <Card.Body>
              <Card.Title as="h3" className="h5">
                Settings
              </Card.Title>
              <p style={descriptionStyle}>Choose how Careerbook contacts you about events.</p>
              <Form.Group controlId="website-account-receive-event-reminders">
                <Form.Label>Receive Event Notification Emails?</Form.Label>
                <Form.Select
                  name="receive_event_reminders"
                  value={updatedFields.receive_event_reminders || 'all'}
                  onChange={(e) => updateField('receive_event_reminders', e.target.value)}
                >
                  <option value="all">for all events</option>
                  <option value="none">for no events</option>
                  <option value="trackedOnly">for attendance-tracked events</option>
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={6} xl={8} className="d-flex">
          <Card className="w-100" style={tileStyle}>
            <Card.Body>
              <Card.Title as="h3" className="h5">
                Address
              </Card.Title>
              <p style={descriptionStyle}>Keep your postal details current for certificates and account records.</p>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-line-one">
                    <Form.Label>Address Line 1</Form.Label>
                    <Form.Control
                      name="line_one"
                      value={updatedFields.line_one || ''}
                      onChange={(e) => updateField('line_one', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-line-two">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                      name="line_two"
                      value={updatedFields.line_two || ''}
                      onChange={(e) => updateField('line_two', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-city">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      name="city"
                      value={updatedFields.city || ''}
                      onChange={(e) => updateField('city', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-county">
                    <Form.Label>County</Form.Label>
                    <Form.Control
                      name="county"
                      value={updatedFields.county || ''}
                      onChange={(e) => updateField('county', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-postcode">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      name="postcode"
                      value={updatedFields.postcode || ''}
                      onChange={(e) => updateField('postcode', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="website-account-country">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      name="country"
                      value={updatedFields.country || ''}
                      onChange={(e) => updateField('country', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <div className="d-flex flex-wrap gap-3 justify-content-end mt-4">
        <Button type="submit" disabled={saving || !hasChanges}>
          {saving ? 'Saving...' : 'Save profile'}
        </Button>
      </div>
      {message && <Alert className="mt-3">{message}</Alert>}
    </Form>
  );
};
